/**
 * Google Analytics 4 loader + helpers for the customer portal.
 *
 * Scope: lazy-load gtag.js, send SPA pageviews, expose an event helper, and
 * keep a consent API so a future banner can flip tracking on/off at runtime.
 *
 * GDPR note: default consent is `granted` because customers reach this portal
 * only via magic-link authentication — they have accepted the ToS, and
 * analytics on authenticated product surfaces typically falls under legitimate
 * interest. If a regional regulator requires an explicit banner, set the env
 * `VITE_GA_CONSENT_DEFAULT=denied` and call `grantAnalyticsConsent()` from the
 * consent UI.
 *
 * Privacy guardrails baked in:
 *   - Query string is stripped from page_location/page_path (magic-link tokens,
 *     Stripe redirects, etc. must never reach GA).
 *   - No user_id / email / customer attributes are ever sent.
 *   - No-ops in dev or when VITE_GA_MEASUREMENT_ID is unset.
 */

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const CONSENT_DEFAULT =
  import.meta.env.VITE_GA_CONSENT_DEFAULT === "denied" ? "denied" : "granted";

type GtagCommand =
  | readonly ["js", Date]
  | readonly ["config", string, Record<string, unknown>?]
  | readonly ["event", string, Record<string, unknown>?]
  | readonly ["consent", "default" | "update", Record<string, string>];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

function pushCommand(command: GtagCommand): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(command);
}

/** Strip query + hash — magic-link tokens and redirect codes must not leak. */
function sanitizePath(pathname: string): string {
  const hashIdx = pathname.indexOf("#");
  const cleanHash = hashIdx === -1 ? pathname : pathname.slice(0, hashIdx);
  const queryIdx = cleanHash.indexOf("?");
  return queryIdx === -1 ? cleanHash : cleanHash.slice(0, queryIdx);
}

function injectScript(measurementId: string): void {
  const src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

export function initAnalytics(): void {
  if (initialized || !MEASUREMENT_ID || typeof window === "undefined") return;
  initialized = true;

  pushCommand([
    "consent",
    "default",
    {
      analytics_storage: CONSENT_DEFAULT,
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    },
  ]);

  pushCommand(["js", new Date()]);
  pushCommand([
    "config",
    MEASUREMENT_ID,
    {
      anonymize_ip: true,
    },
  ]);

  injectScript(MEASUREMENT_ID);
}

export function trackPageView(pathname: string): void {
  if (!initialized || !MEASUREMENT_ID) return;
  const path = sanitizePath(pathname);
  pushCommand([
    "event",
    "page_view",
    {
      page_path: path,
      page_location: `${window.location.origin}${path}`,
      page_title: document.title,
    },
  ]);
}

export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!initialized || !MEASUREMENT_ID) return;
  pushCommand(["event", name, params]);
}

export function grantAnalyticsConsent(): void {
  pushCommand(["consent", "update", { analytics_storage: "granted" }]);
}

export function revokeAnalyticsConsent(): void {
  pushCommand(["consent", "update", { analytics_storage: "denied" }]);
}
